/**
 * Auth API Service - Handles all authentication API calls
 * Follows "Joyful Professional" design principles with friendly error messages
 */

const API_BASE = process.env.REACT_APP_RESOURCE_API || 'https://neev-api.onrender.com';

class AuthAPI {
  /**
   * Login with phone and password
   * @param {string} phone - User phone number
   * @param {string} password - User password
   * @returns {Promise<Object>} { token, refreshToken, expiresAt, user }
   */
  async login(phone, password) {
    try {
      const response = await fetch(`${API_BASE}/api/v1/user-auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Friendly error messages (no jargon)
        const errorMessage = data.error || data.details || 'Login failed. Please check your phone number and password.';
        throw new Error(errorMessage);
      }

      // Extract data from response
      // Backend returns { data: { token, refresh_token, expires_at, user } }
      const payload = data.data || data;
      const { token, refresh_token, expires_at, user } = payload;
      
      return {
        token,
        refreshToken: refresh_token,
        expiresAt: expires_at,
        // Ensure user is extracted correctly
        user: user || payload.user,
      };
    } catch (error) {
      if (error.message) {
        throw error;
      }
      throw new Error('Unable to connect. Please check your internet connection.');
    }
  }

  /**
   * Logout - invalidate current session
   * @param {string} token - Access token
   * @returns {Promise<void>}
   */
  async logout(token) {
    try {
      await fetch(`${API_BASE}/api/v1/user-auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
    } catch (error) {
      // Don't throw error on logout failure - still clear local tokens
      console.error('Logout API call failed:', error);
    }
  }

  /**
   * Refresh access token using refresh token
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<Object>} { token, refreshToken, expiresAt, user }
   */
  async refreshToken(refreshToken) {
    try {
      const response = await fetch(`${API_BASE}/api/v1/user-auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Session expired. Please log in again.');
      }

      // Backend returns { data: { token, refresh_token, expires_at, user } }
      const payload = data.data || data;
      const { token, refresh_token, expires_at, user } = payload;
      
      return {
        token,
        refreshToken: refresh_token,
        expiresAt: expires_at,
        // Ensure user is extracted correctly (could be nested)
        user: user || payload.user,
      };
    } catch (error) {
      if (error.message) {
        throw error;
      }
      throw new Error('Unable to refresh session. Please log in again.');
    }
  }

  /**
   * Request password reset token
   * @param {string} phone - User phone number
   * @returns {Promise<string>} Reset token (for testing - in production, token sent via SMS/Email)
   */
  async forgotPassword(phone) {
    try {
      const response = await fetch(`${API_BASE}/api/v1/user-auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Don't reveal if user exists (security best practice)
        // Always return success message
        return null;
      }

      // In production, token is sent via SMS/Email, not returned
      // For testing, return token if available
      return data.token || null;
    } catch (error) {
      // Don't reveal error - always return success for security
      return null;
    }
  }

  /**
   * Reset password using reset token
   * @param {string} token - Password reset token
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   */
  async resetPassword(token, newPassword) {
    try {
      const response = await fetch(`${API_BASE}/api/v1/user-auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, new_password: newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.error || data.details || 'Password reset failed. The link may have expired.';
        throw new Error(errorMessage);
      }
    } catch (error) {
      if (error.message) {
        throw error;
      }
      throw new Error('Unable to reset password. Please try again.');
    }
  }

  /**
   * Get user assignments (schools, classes, divisions)
   * @param {string} token - Access token
   * @param {string} academicYear - Academic year (optional)
   * @returns {Promise<Array>} Array of user assignments
   */
  async getUserAssignments(token, academicYear = null) {
    try {
      const query = academicYear ? `?academic_year=${academicYear}` : '';
      const response = await fetch(`${API_BASE}/api/v1/user-auth/assignments${query}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to fetch assignments');
      }

      return data.assignments || [];
    } catch (error) {
      if (error.message) {
        throw error;
      }
      throw new Error('Unable to fetch assignments. Please check your internet connection.');
    }
  }

  /**
   * Get current authenticated user
   * @param {string} token - Access token
   * @returns {Promise<Object>} User object
   */
  async getCurrentUser(token) {
    try {
      const response = await fetch(`${API_BASE}/api/v1/user-auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to get user information');
      }

      // Backend returns { user: {...} }, so extract the user object
      // Also handle case where login response wraps it in data: { user: {...} }
      return data.user || data.data?.user || data.data || data;
    } catch (error) {
      if (error.message) {
        throw error;
      }
      throw new Error('Unable to get user information. Please try again.');
    }
  }

  /**
   * Switch active role
   * @param {string} token - Access token
   * @param {string} roleCode - Role code to switch to
   * @returns {Promise<Object>} { token, expiresAt, user }
   */
  async switchRole(token, roleCode) {
    try {
      const response = await fetch(`${API_BASE}/api/v1/user-auth/switch-role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ role_code: roleCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.error || data.details || 'Unable to switch role. Please try again.';
        throw new Error(errorMessage);
      }

      const { token: newToken, expires_at, user } = data.data || data;
      
      return {
        token: newToken,
        expiresAt: expires_at,
        user,
      };
    } catch (error) {
      if (error.message) {
        throw error;
      }
      throw new Error('Unable to switch role. Please try again.');
    }
  }

  /**
   * Get user's active sessions
   * @param {string} token - Access token
   * @returns {Promise<Array>} Array of session objects
   */
  async getSessions(token) {
    try {
      const response = await fetch(`${API_BASE}/api/v1/user-auth/sessions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to get sessions');
      }

      return data.sessions || [];
    } catch (error) {
      if (error.message) {
        throw error;
      }
      throw new Error('Unable to get sessions. Please try again.');
    }
  }

  /**
   * Revoke a specific session
   * @param {string} token - Access token
   * @param {number} sessionId - Session ID to revoke
   * @returns {Promise<void>}
   */
  async revokeSession(token, sessionId) {
    try {
      const response = await fetch(`${API_BASE}/api/v1/user-auth/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to revoke session');
      }
    } catch (error) {
      if (error.message) {
        throw error;
      }
      throw new Error('Unable to revoke session. Please try again.');
    }
  }

  /**
   * Revoke all other sessions (keep current)
   * @param {string} token - Access token
   * @returns {Promise<number>} Number of sessions revoked
   */
  async revokeAllOtherSessions(token) {
    try {
      const response = await fetch(`${API_BASE}/api/v1/user-auth/sessions/all`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to revoke sessions');
      }

      return data.revoked_count || 0;
    } catch (error) {
      if (error.message) {
        throw error;
      }
      throw new Error('Unable to revoke sessions. Please try again.');
    }
  }
}

// Export singleton instance
export default new AuthAPI();

